import { IApiUserGroup } from 'multirpc-sdk';
import { createSelector } from 'reselect';

import { ESeconds } from 'modules/common/constants/const';
import { IAccount } from 'modules/common/types';
import { MultiService } from 'modules/api/MultiService';
import { RequestType, web3Api } from 'store/queries';
import { createQuerySelectors } from 'store/utils/createQuerySelectors';
import { createQueryFnWithErrorHandler } from 'store/utils/createQueryFnWithErrorHandler';

import { convertGroupsIntoAccounts } from '../utils/convertGroupsIntoAccounts';

// The endpoint name is listed in endpointsSerializedByParams constant
// in packages/protocol/src/store/queries/index.ts file.
// If the name has changed it should be reflected there as well.
export const {
  endpoints: { fetchTeamAccounts },
  useFetchTeamAccountsQuery,
  useLazyFetchTeamAccountsQuery,
  usePrefetch: useTeamAccountsPrefetch,
} = web3Api.injectEndpoints({
  endpoints: build => ({
    fetchTeamAccounts: build.query<IApiUserGroup[], void>({
      providesTags: [RequestType.TeamAccounts],
      keepUnusedDataFor: ESeconds.Day, // no need to refetch more often
      queryFn: createQueryFnWithErrorHandler({
        queryFn: async () => {
          const api = MultiService.getService().getAccountingGateway();

          const { groups: data } = await api.getUserGroups();

          return { data };
        },
      }),
    }),
  }),
});

export const fetchTeamAccountsFallback: IAccount[] = [];

const selectors = createQuerySelectors({
  endpoint: fetchTeamAccounts,
});

export const {
  selectInitalLoadingCachedByParams: selectTeamAccountsInitialLoading,
  selectLoadingCachedByParams: selectTeamAccountsLoading,
  selectStateCachedByParams: selectTeamAccountsState,
} = selectors;

export const selectTeamAccounts = createSelector(
  selectors.selectDataCachedByParams,
  groups =>
    groups
      ? convertGroupsIntoAccounts(groups).sort((a, b) =>
          a.name.localeCompare(b.name),
        )
      : fetchTeamAccountsFallback,
);
